import { portals, type IdempotencyKey, type PortalInviteList, type PortalMemberPage, type PortalPage, type PortalRead, type PortalTeamRead } from "@baton/api";
import { keys, normaliseQuery, type CursorPageParam, type Runtime } from "@baton/core";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

/**
 * 028 `contracts/http-api.md` § Portals and § Portal people, `specs/015-web-frontend/contracts/screens.md`
 * § 028 Client portal: every read the Tickets screens make, keyed under the workspace's one `portals`
 * prefix (`@baton/core`'s scheme), so a `portal.*` event re-reads them with one invalidation. Every
 * write is confirmed rather than optimistic, and invalidates that prefix on its answer.
 *
 * A browser route names a portal by its client (`/{ws}/tickets/{account}`), as the client page does,
 * so the page finds the portal through the list filtered to the client (`?account_id=`), which
 * answers both standings: the worker's Team shape and a client member's own shape of the portals
 * they belong to.
 */
export const PORTAL_PAGE_LIMIT = 100;

const nextCursor = (last: { readonly next_cursor: string | null }): CursorPageParam | undefined => (last.next_cursor === null ? undefined : { cursor: last.next_cursor });

/** Whether a portal read is the Team's shape, which a portal worker reads (`research.md` D-2805). */
export function isTeamRead(portal: PortalRead): portal is PortalTeamRead {
  return Object.hasOwn(portal, "version");
}

/** Every portal of the Team the caller reaches, every page of it: a worker's whole list, open and closed, or a client member's own open portals. */
export function portalsQuery(runtime: Runtime, ws: string) {
  return infiniteQueryOptions({
    queryKey: keys.portalsList(ws, normaliseQuery({})),
    initialPageParam: { cursor: null },
    getNextPageParam: nextCursor,
    queryFn: async ({ pageParam, signal }): Promise<PortalPage> =>
      (await portals.list(runtime.signedIn, { params: { ws }, query: { limit: PORTAL_PAGE_LIMIT, ...(pageParam.cursor === null ? {} : { cursor: pageParam.cursor }) }, signal })).data,
  });
}

/** The portal of one client, or null while it has none (or none the caller reaches). */
export function portalOfAccountQuery(runtime: Runtime, ws: string, accountId: string) {
  return queryOptions({
    queryKey: keys.portalsList(ws, normaliseQuery({ account_id: accountId })),
    queryFn: async ({ signal }): Promise<PortalRead | null> => (await portals.list(runtime.signedIn, { params: { ws }, query: { account_id: accountId }, signal })).data.items[0] ?? null,
  });
}

/** One portal's members, the live ones or, with `includeRemoved`, the removed beside them. */
export function portalMembersQuery(runtime: Runtime, ws: string, portalId: string, includeRemoved = false) {
  return infiniteQueryOptions({
    queryKey: keys.portalMembers(ws, portalId, includeRemoved),
    initialPageParam: { cursor: null },
    getNextPageParam: nextCursor,
    queryFn: async ({ pageParam, signal }): Promise<PortalMemberPage> =>
      (
        await portals.members(runtime.signedIn, {
          params: { ws, portal: portalId },
          query: { limit: PORTAL_PAGE_LIMIT, ...(includeRemoved ? { include_removed: true } : {}), ...(pageParam.cursor === null ? {} : { cursor: pageParam.cursor }) },
          signal,
        })
      ).data,
  });
}

/** One portal's open invites, expired ones included, newest first. */
export function portalInvitesQuery(runtime: Runtime, ws: string, portalId: string) {
  return queryOptions({
    queryKey: keys.portalInvites(ws, portalId),
    queryFn: async ({ signal }): Promise<PortalInviteList> => (await portals.invites(runtime.signedIn, { params: { ws, portal: portalId }, signal })).data,
  });
}

/** Re-read every portal read of the Team, which is what each write here changes. */
export async function refreshPortals(runtime: Runtime, ws: string): Promise<void> {
  await runtime.queryClient.invalidateQueries({ queryKey: keys.portals(ws) });
}

/** Open the client's portal with its first invite (`POST /portals`, FR-028-005 as amended 2026-10-21); a second open of one client is refused 409 `portal_exists`, so a repeat makes no second portal. */
export async function openPortal(runtime: Runtime, ws: string, accountId: string, first: { readonly email: string; readonly display_name: string }, idempotencyKey: IdempotencyKey): Promise<void> {
  await portals.create(runtime.signedIn, { params: { ws }, body: { account_id: accountId, ...first }, idempotencyKey });
  await refreshPortals(runtime, ws);
}

/**
 * Close a client's tickets at the version the worker read (FR-028-009): its people lose them, every
 * open invite is revoked and nothing is emailed. The portal's reads are read again whether the close
 * is answered or refused, so a 409 (closed meanwhile) or a 412 (changed meanwhile) leaves every screen
 * drawing the portal as it now stands.
 */
export async function closePortal(runtime: Runtime, ws: string, portal: PortalTeamRead): Promise<void> {
  try {
    await portals.close(runtime.signedIn, { params: { ws, portal: portal.id }, version: portal.version });
  } finally {
    await refreshPortals(runtime, ws);
  }
}

/** Reopen a closed client's tickets at the version the worker read (FR-028-009), its members admitted again; read again however it is answered, as the close is. */
export async function reopenPortal(runtime: Runtime, ws: string, portal: PortalTeamRead): Promise<void> {
  try {
    await portals.reopen(runtime.signedIn, { params: { ws, portal: portal.id }, version: portal.version });
  } finally {
    await refreshPortals(runtime, ws);
  }
}

/** Follow or stop following a portal, which is the caller's own row and records `watched` or `unwatched`. */
export async function setFollowing(runtime: Runtime, ws: string, portalId: string, following: boolean): Promise<void> {
  if (following) await portals.follow(runtime.signedIn, { params: { ws, portal: portalId } });
  else await portals.unfollow(runtime.signedIn, { params: { ws, portal: portalId } });
  await refreshPortals(runtime, ws);
}

/** Invite one address under one name, which emails the invite. */
export async function invitePerson(runtime: Runtime, ws: string, portalId: string, body: { readonly email: string; readonly display_name: string }, idempotencyKey: IdempotencyKey): Promise<void> {
  await portals.invite(runtime.signedIn, { params: { ws, portal: portalId }, body, idempotencyKey });
  await refreshPortals(runtime, ws);
}

export async function resendInvite(runtime: Runtime, ws: string, portalId: string, inviteId: string): Promise<void> {
  await portals.resendInvite(runtime.signedIn, { params: { ws, portal: portalId, invite: inviteId } });
  await refreshPortals(runtime, ws);
}

export async function revokeInvite(runtime: Runtime, ws: string, portalId: string, inviteId: string): Promise<void> {
  await portals.revokeInvite(runtime.signedIn, { params: { ws, portal: portalId, invite: inviteId } });
  await refreshPortals(runtime, ws);
}

export async function removeMember(runtime: Runtime, ws: string, portalId: string, userId: string): Promise<void> {
  await portals.removeMember(runtime.signedIn, { params: { ws, portal: portalId, user: userId } });
  await refreshPortals(runtime, ws);
}
